import React from 'react';
import { Tooltip, Typography } from 'antd';
import { INGREDIENTS } from 'constants/Scope3Constants/category1.constants';
import { FieldType } from 'enums/FieldTypeEnums';
import { Category1Types } from 'enums/category1Enums';
import { CategoryOptions } from 'shared/components/CreateProductModal/ProductModalForm';
import { FieldsRowProps } from 'shared/components/FieldsRow';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import styles from './category1.module.scss';
import { IndexBadge } from 'shared/components/IndexBadge';
import { UseFieldArrayRemove, UseFormSetValue } from 'react-hook-form';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import { ScopeThreeCategoryOne } from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import { FieldProps } from 'enums/RawMaterialEnums';
import {
  categoryTypeFieldMap,
  getFieldName,
} from '../Category4&9/Category4Layout/category4LayoutData';
import { DefaultOptionType } from 'antd/lib/select';
import {
  currencyOptions,
  quantityUnits,
} from 'constants/Scope3Constants/category4.constants';
import { allowOnlyNumbersUptoN } from 'shared/utils/convertFormData';
import { BestFitRegion } from 'models/BestFitRegion/bestFitRegion.model';

export interface Category1LayoutDataProps {
  index: number;
  type: Category1Types;
  nameOptions?: CategoryOptions[];
  bestFitRegionOptions?: BestFitRegion[];
  originsLength?: number;
  ingredientsLength?: number;
  remove?: UseFieldArrayRemove;
  originIndex?: number;
  appendOrigin?: () => void;
  removeOrigin?: (index?: number) => void;
  setIsAddIngredient?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddRegion?: React.Dispatch<React.SetStateAction<boolean>>;
  setValue?: UseFormSetValue<ScopeThreeCategoryOne>;
  handleChange?: () => void;
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string | string[]>>;
  loading?: boolean;
}
const { LABELS, GUTTER_VALUE, ADD_ORIGIN } = INGREDIENTS;

const {
  INGREDIENT,
  PACKAGING,
  OTHER,
  ORIGIN,
  PACKAGING_ORIGIN,
  OTHER_ORIGIN,
  BEST_FIT_REGION,
  QUANTITY,
  UNIT,
  COST,
} = LABELS;
const { OTHERS, PACKAGING_MATERIALS } = Category1Types;
const {
  REQUEST_OTHER,
  REQUEST_PACKAGING_MATERIAL,
  REQUEST_INGREDIENT,
  REQUEST_REGION,
} = FieldProps;

export const getFormData = (
  props: Category1LayoutDataProps
): FieldsRowProps => {
  const {
    type,
    index,
    nameOptions,
    bestFitRegionOptions,
    originsLength,
    ingredientsLength,
    remove,
    appendOrigin,
    setIsAddIngredient,
    setIsAddRegion,
    handleChange,
    setActiveTabKey,
    loading,
  } = props;
  const hasMultipleOrigins = originsLength && originsLength > 1;

  const getName = (name: string) => {
    return getFieldName(type, index, name);
  };

  const removeHandler = () => {
    remove?.(index);
    if (ingredientsLength === 1 && index === 0) setActiveTabKey?.('4');
  };

  const handleOpenRequestIngredientModal = () => setIsAddIngredient?.(true);

  const handleOpenRequestRegionModal = () => setIsAddRegion?.(true);
  const typeValue = categoryTypeFieldMap[type];
  const isOthersSection = type === OTHERS;
  const ingredientFieldLabel =
    type === OTHERS
      ? OTHER
      : type === PACKAGING_MATERIALS
      ? PACKAGING
      : INGREDIENT;
  const originFieldLabel =
    type === OTHERS
      ? OTHER_ORIGIN
      : type === PACKAGING_MATERIALS
      ? PACKAGING_ORIGIN
      : ORIGIN;

  return {
    data: [
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div className={styles.numberContainer}>
            <IndexBadge index={index} />
          </div>
        ),
        span: 1,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name: getName('activityLabel'),
          options: nameOptions,
          label: ingredientFieldLabel,
          showSearch: true,
          placeholderstring: ingredientFieldLabel,
          onChange: handleChange,
          dropdownRender: (menu: CategoryOptions) => (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleOpenRequestIngredientModal}>
                <Add />
                {type === OTHERS
                  ? REQUEST_OTHER
                  : type === PACKAGING_MATERIALS
                  ? REQUEST_PACKAGING_MATERIAL
                  : REQUEST_INGREDIENT}
              </div>
              {menu}
            </>
          ),
        },
        span: 5,
      },
      {
        type: FieldType.LOCATION_MODAL,
        fieldProps: {
          type,
          name:
            originsLength === 1
              ? `${typeValue}.${index}.origins.${0}.addressLabel`
              : `${typeValue}.${index}.bestFitRegion`,
          label: originFieldLabel,
          placeholderstring: originFieldLabel,
          disabled: !!hasMultipleOrigins,
          index,
          suffix: <MapSearch />,
        },
        span: 5,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name:
            originsLength === 1
              ? `${typeValue}.${index}.origins.${0}.activityMappingRegionSpecId`
              : `${typeValue}.${index}.bestFitRegion`,
          label: BEST_FIT_REGION,
          placeholderstring: BEST_FIT_REGION,
          filterOption: (inputValue?: string, option?: DefaultOptionType) => {
            const categoryOption = option as CategoryOptions;
            return (
              categoryOption?.label
                ?.toLowerCase()
                .includes(inputValue?.toLowerCase() || '') || false
            );
          },
          options: bestFitRegionOptions,
          disabled: !!hasMultipleOrigins,
          showSearch: true,
          loading: !!loading,
          dropdownRender: (optionsMenu: CategoryOptions) => (
            <>
              <div
                className={styles.dropdownCreateButton}
                onClick={handleOpenRequestRegionModal}>
                <Add /> {REQUEST_REGION}
              </div>
              {optionsMenu}
            </>
          ),
        },
        span: 5,
      },
      {
        type: FieldType.INPUT,
        fieldProps: {
          name: getName('quantity'),
          label: isOthersSection ? COST : QUANTITY,
          placeholderstring: isOthersSection ? COST : QUANTITY,
          onKeyDown: allowOnlyNumbersUptoN(isOthersSection ? 2 : 4),
          isNullable: true,
        },
        span: 2,
      },
      {
        type: FieldType.SELECT,
        fieldProps: {
          name: getName('unit'),
          label: UNIT,
          placeholderstring: UNIT,
          options: isOthersSection ? currencyOptions : quantityUnits,
        },
        span: 3,
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div className={styles.addOrigin} onClick={appendOrigin}>
            <Tooltip title={ADD_ORIGIN}>
              <Typography.Paragraph>
                <AddIcon />
              </Typography.Paragraph>
            </Tooltip>
          </div>
        ),
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div className={styles.deleteContainer} onClick={removeHandler}>
            <DeleteButton />
          </div>
        ),
      },
    ],
    rowGutter: GUTTER_VALUE,
  };
};
